import React, { useCallback, useEffect, useState } from 'react';
import { ModalMutateProductProps, MutateFormFields } from './types';
import { Button } from '@vtbhome/juicyfront';
import { Body, CopyCodeWrapper, Footer, InputGroup } from './modal-mutate-product.styles';
import { Field, Form } from 'react-final-form';
import { validate } from '../../lib/form-validate';
import { Typography } from '@shared/ui/typography/typography';
import { InputText } from '@shared/ui/input-text/input-text';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import {
    useCreateProductMutation,
    useProductTypesInfinityListQuery,
    useUpdateProductMutation,
} from '@entities/product/api/product-api';
import { CACHE_KEY_PRODUCT_CREATE, CACHE_KEY_PRODUCT_UPDATE } from '@entities/product/config/api-constants';
import { InputNumber } from '@shared/ui/input-number/input-number';
import { Select } from '@shared/ui/select/select';
import { CatalogsListDataRequest } from '@entities/catalog/api/catalog-types';
import { DEFAULT_CATALOGS_LIST_PARAMETERS } from '@entities/catalog/config/store-constants';
import { useCatalogInfinityListQuery } from '@entities/catalog/api/catalog-api';
import { DEFAULT_PRODUCT_TYPES_INFINITY_LIST_PARAMETERS } from '@entities/product/config/store-constants';
import { useProductFullInfoSharedQuery } from '@entities/product/libs/hooks/use-product-full-info-shared-query/use-product-full-info-shared-query';
import { Textarea } from '@shared/ui/textarea/textarea';
import { Datepicker } from '@shared/ui/datepicker/datepicker';
import { RadioGroup } from '@shared/ui/radio-group/radio-group';
import { useNormalizeInitialValues } from '../../lib/normalize-initial-values';
import { normalizeSubmitValues } from '../../lib/normalize-submit-values';
import { EditFormKeys } from '../../config/form-constants';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { useCatalogSelect } from '../../lib/hooks/use-catalog-options/use-catalog-options';
import { ProductTypesListDataRequest } from '@entities/product/api/product-types';
import { useProductTypesSelect } from '../../lib/hooks/use-product-types-options/use-product-types-options';
import { InputFileSearch } from '@shared/ui/input-file-search/input-file-search';
import { Modal } from '@shared/ui/modal/modal';
import { CopyLink } from '@shared/ui/copy-link/copy-link';
import { ROUTE_ADMIN_PRODUCT_BASE } from '@shared/config/routes/routes';
import { useCatalogsListSharedQuery } from '@entities/catalog/libs/hooks/use-catalogs-list-shared-query/use-catalogs-list-shared-query';
import { InputQuill } from '@shared/ui/input-quill/input-quill';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';
import { useCrmInterestsProductsInfinityQuery } from '@entities/product/libs/hooks/use-crm-interests-products-infinity-query/use-crm-interests-products-infinity-query';
import { useCrmProductsInfinityQuery } from '@entities/product/libs/hooks/use-crm-products-infinity-query/use-crm-products-infinity-query';
import { checkProductTypeIsLicense } from '@features/product/mutate-product/lib/check-product-type-is-license';
import { UTMInfoKeysEnum } from '@entities/current-user/api/credentials-user-types';
import { Checkbox } from '@shared/ui/checkbox/checkbox';

export const ModalMutateProduct: React.FC<ModalMutateProductProps> = ({
    productId,
    isShow,
    action = 'create',
    additionalProductInfo,
    onClose,
    onAccept,
}) => {
    /* Catalogs */
    const [catalogsListParameters, setCatalogsListParameters] = useState<CatalogsListDataRequest>({
        ...DEFAULT_CATALOGS_LIST_PARAMETERS,
        take: 10,
    });
    const {
        data: catalogsList,
        isLoading: isCatalogsListLoading,
        isFetching: isCatalogsListFetching,
    } = useCatalogInfinityListQuery(catalogsListParameters);

    const {
        catalogOptions,
        catalogEmptyOption,
        isShowEmptyLoaderCatalogs,
        infinityScrollCatalogsProps,
        onSearchCatalogs,
    } = useCatalogSelect({
        catalogsData: catalogsList,
        isLoading: isCatalogsListLoading,
        isFetching: isCatalogsListFetching,
        setCatalogsListParameters,
    });

    /* Product */
    const [getProductInfo, getProductInfoResult] = useProductFullInfoSharedQuery(productId);
    const [createProduct, createProductResult] = useCreateProductMutation({
        fixedCacheKey: CACHE_KEY_PRODUCT_CREATE,
    });
    const [editProduct, editProductResult] = useUpdateProductMutation({
        fixedCacheKey: CACHE_KEY_PRODUCT_UPDATE,
    });
    const [, , resetCatalogsList] = useCatalogsListSharedQuery({});
    const productInfo = getProductInfoResult.data;
    const error = getQueryError(getProductInfoResult);

    /* Product type */
    const [productTypesListParameters, setProductTypesListParameters] = useState<ProductTypesListDataRequest>({
        ...DEFAULT_PRODUCT_TYPES_INFINITY_LIST_PARAMETERS,
        take: 10,
    });

    const {
        data: productTypesList,
        isLoading: isProductTypesListLoading,
        isFetching: isProductTypesListFetching,
    } = useProductTypesInfinityListQuery(productTypesListParameters);

    const {
        productTypeOptions,
        productTypeEmptyOption,
        isShowEmptyLoaderProductTypes,
        infinityScrollProductTypesProps,
        onSearchProductTypes,
    } = useProductTypesSelect({
        productTypesData: productTypesList,
        isLoading: isProductTypesListLoading,
        isFetching: isProductTypesListFetching,
        setProductTypesListParameters,
    });

    /* CRM products */
    const {
        options: crmProductsOptions,
        emptyOption: crmProductsEmptyOption,
        isShowEmptyLoader: isCRMProductsShowEmptyLoader,
        onSearch: onCRMProductsSearch,
        infinityScrollProps: crmProductsInfinityScrollProps,
    } = useCrmProductsInfinityQuery();

    /* CRM interests products */
    const {
        options: crmInterestsProductsOptions,
        emptyOption: crmInterestsProductsEmptyOption,
        isShowEmptyLoader: isCRMInterestsProductsShowEmptyLoader,
        onSearch: onCRMInterestsProductsSearch,
        infinityScrollProps: crmInterestsProductsInfinityScrollProps,
    } = useCrmInterestsProductsInfinityQuery();

    const formInitialValues = useNormalizeInitialValues(productInfo, additionalProductInfo);
    const formRef = useResetModalForm<MutateFormFields, typeof formInitialValues>(isShow);

    const isCreateAction = action === 'create';
    const isLoadingInfo =
        getProductInfoResult.isFetching ||
        getProductInfoResult.isLoading ||
        (getProductInfoResult.isUninitialized && !isCreateAction);
    const isLoadingMutate = editProductResult.isLoading || createProductResult.isLoading;

    const handleSubmit = async (fields: MutateFormFields) => {
        let isError = false;
        if (productId && action === 'edit') {
            const result = await editProduct({
                id: productId,
                ...(await normalizeSubmitValues(fields)),
            });
            isError = Boolean(result.error);
            getProductInfo(productId, false);
        } else if (isCreateAction) {
            const result = await createProduct(await normalizeSubmitValues(fields));
            isError = Boolean(result.error);
        }
        if (!isError) {
            resetCatalogsList();
            onAccept();
        }
    };

    useEffect(() => {
        if (isShow && productId) {
            getProductInfo(productId);
        }
    }, [isShow, productId]);

    const formValidate = useCallback(
        (fields: MutateFormFields) => validate(fields, productTypesList),
        [productTypesList]
    );

    return (
        <Form
            onSubmit={handleSubmit}
            validate={formValidate}
            initialValues={formInitialValues}
            render={(form) => {
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShow}
                        variant='drawer'
                        size='l'
                        header={
                            <Typography size='h6'>{`${isCreateAction ? 'Создание' : 'Редактирование'} продукта`}</Typography>
                        }
                        footer={
                            <Footer>
                                <Button disabled={isLoadingMutate} buttonType='light' onClick={onClose}>
                                    Отменить
                                </Button>
                                <Button
                                    preloader={isLoadingMutate}
                                    disabled={isLoadingMutate}
                                    buttonType='primary'
                                    onClick={form.handleSubmit}>
                                    Сохранить
                                </Button>
                            </Footer>
                        }
                        onClose={onClose}>
                        <Body>
                            <NetworkStateNew
                                size='s'
                                isLoading={isLoadingInfo}
                                isError={Boolean(error)}
                                isInitialized={!isLoadingInfo}
                                isFiltered={false}
                                hasData={isCreateAction || Boolean(productInfo)}
                                errorProps={{
                                    title: 'Ошибка загрузки продуктов',
                                    description: error?.message,
                                }}>
                                <InputGroup>
                                    <Field
                                        name={EditFormKeys.Name}
                                        render={({ input, meta }) => (
                                            <InputText
                                                disabled={isLoadingMutate}
                                                label='Название продукта'
                                                placeholder='Введите название продукта'
                                                required
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Description}
                                        render={({ input, meta }) => (
                                            <InputQuill
                                                disabled={isLoadingMutate}
                                                label='Описание'
                                                placeholder='Введите описание продукта'
                                                required
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                onChange={(value) => {
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.ProductInfoUrl}
                                        render={({ input, meta }) => (
                                            <InputText
                                                disabled={isLoadingMutate}
                                                label='Ссылка на продукт'
                                                placeholder='Ввыдите ссылку'
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Code}
                                        render={({ input, meta }) => (
                                            <CopyCodeWrapper>
                                                <InputText
                                                    disabled={isLoadingMutate}
                                                    label='Системное имя'
                                                    placeholder='Введите системное имя'
                                                    required
                                                    value={input.value}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                                {!isCreateAction && (
                                                    <CopyLink
                                                        url={`${window.location.origin}${ROUTE_ADMIN_PRODUCT_BASE}/${productId}?${UTMInfoKeysEnum.LKProduct}=${productInfo?.code}`}
                                                        tooltipMessage='Скопировать ссылку на продукт'
                                                        successMessage='Ссылка на продукт скопирована'
                                                        errorMessage='Не удалось скопировать ссылку на продукт'
                                                    />
                                                )}
                                            </CopyCodeWrapper>
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.CRMInterestCode}
                                        render={({ input, meta }) => {
                                            return (
                                                <Select
                                                    isAsync
                                                    clearable
                                                    disabled={isLoadingMutate}
                                                    label='Продукт интереса CRM'
                                                    placeholder='Выберите продукт интереса CRM'
                                                    isShowPreloaderInList={isCRMInterestsProductsShowEmptyLoader}
                                                    onSearch={onCRMInterestsProductsSearch}
                                                    infinityScrollProps={crmInterestsProductsInfinityScrollProps}
                                                    values={input.value || crmInterestsProductsEmptyOption}
                                                    options={crmInterestsProductsOptions}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            );
                                        }}
                                    />
                                    <Field name={EditFormKeys.Type} subscription={{ value: true }}>
                                        {({ input: { value } }) => (
                                            <Field
                                                name={EditFormKeys.CRMProductExternalId}
                                                render={({ input, meta }) => {
                                                    return (
                                                        <Select
                                                            required={checkProductTypeIsLicense(
                                                                value[0]?.value,
                                                                productTypesList
                                                            )}
                                                            isAsync
                                                            clearable
                                                            disabled={isLoadingMutate}
                                                            label='Продукт CRM'
                                                            placeholder='Выберите продукт CRM'
                                                            isShowPreloaderInList={isCRMProductsShowEmptyLoader}
                                                            onSearch={onCRMProductsSearch}
                                                            infinityScrollProps={crmProductsInfinityScrollProps}
                                                            values={input.value || crmProductsEmptyOption}
                                                            options={crmProductsOptions}
                                                            error={meta.touched && meta.error}
                                                            onChange={(options) => {
                                                                if (!options.length) {
                                                                    form.form.change(
                                                                        EditFormKeys.CRMNeedOpportunity,
                                                                        false
                                                                    );
                                                                }
                                                                input.onChange(options);
                                                            }}
                                                        />
                                                    );
                                                }}
                                            />
                                        )}
                                    </Field>
                                    <Field
                                        name={EditFormKeys.CRMNeedOpportunity}
                                        render={({ input, meta }) => {
                                            const withoutPrice = form.form.getFieldState(
                                                EditFormKeys.WithoutPrice
                                            )?.value;

                                            const crmProducts =
                                                form.form.getFieldState(EditFormKeys.CRMProductExternalId)?.value || [];

                                            const value = form.form.getFieldState(
                                                EditFormKeys.CRMNeedOpportunity
                                            )?.value;

                                            return (
                                                <Checkbox
                                                    {...input}
                                                    disabled={isLoadingMutate || withoutPrice || !crmProducts.length}
                                                    label='Автоматическая конвертация лида в сделку CRM'
                                                    checked={value}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            );
                                        }}
                                    />
                                    <Field
                                        name={EditFormKeys.Image}
                                        render={({ input, meta }) => (
                                            <InputFileSearch
                                                {...input}
                                                disabled={isLoadingMutate}
                                                label='Добавить изображение'
                                                selectLabel='Прикрепление изображения'
                                                selectPlaceholder='Выберите изображение из списка'
                                                max={1}
                                                accept='.jpeg,.jpg,.png'
                                                placeholderLocalInput={
                                                    <Typography>
                                                        {`Перетащите в эту область или `}
                                                        <Typography color='bright_blue'>загрузите</Typography>
                                                        {`\nне более 1 файла, формата JPEG, JPG, PNG`}
                                                    </Typography>
                                                }
                                                error={meta.touched && meta.error}
                                                onChange={(file) => {
                                                    input.onChange(file);
                                                }}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Price}
                                        render={({ input, meta }) => {
                                            const withoutPrice = form.form.getFieldState(
                                                EditFormKeys.WithoutPrice
                                            )?.value;

                                            return (
                                                <InputNumber
                                                    {...input}
                                                    disabled={isLoadingMutate || withoutPrice}
                                                    label='Стоимость'
                                                    placeholder='Введите стоимость'
                                                    required={!withoutPrice}
                                                    min={0}
                                                    max={9999999}
                                                    error={meta.touched && meta.error}
                                                />
                                            );
                                        }}
                                    />
                                    <Field
                                        name={EditFormKeys.WithoutPrice}
                                        render={({ input, meta }) => {
                                            const value = form.form.getFieldState(EditFormKeys.WithoutPrice)?.value;

                                            return (
                                                <Checkbox
                                                    {...input}
                                                    disabled={isLoadingMutate}
                                                    label='Стоимость не указана'
                                                    checked={value}
                                                    error={meta.touched && meta.error}
                                                    onChange={(e) => {
                                                        form.form.change(EditFormKeys.Price, '0');
                                                        form.form.change(EditFormKeys.CRMNeedOpportunity, false);
                                                        input.onChange(e);
                                                    }}
                                                />
                                            );
                                        }}
                                    />

                                    <RadioGroup
                                        name={EditFormKeys.Active}
                                        disabled={isLoadingMutate}
                                        label='Состояние продукта'
                                        fields={[
                                            {
                                                label: 'Активный',
                                                value: 'true',
                                            },
                                            {
                                                label: 'Скрытый',
                                                value: 'false',
                                            },
                                        ]}
                                    />
                                    <Field
                                        name={EditFormKeys.EmailDomainRestricted}
                                        render={({ input, meta }) => {
                                            const value = form.form.getFieldState(
                                                EditFormKeys.EmailDomainRestricted
                                            )?.value;

                                            return (
                                                <Checkbox
                                                    {...input}
                                                    disabled={isLoadingMutate}
                                                    label='Сделка только с корпоративным email'
                                                    checked={value}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            );
                                        }}
                                    />
                                    <Field
                                        name={EditFormKeys.Type}
                                        render={({ input, meta }) => {
                                            return (
                                                <Select
                                                    required
                                                    isAsync
                                                    clearable
                                                    disabled={isLoadingMutate}
                                                    label='Тип продукта'
                                                    placeholder='Выберите тип продукта'
                                                    isShowPreloaderInList={isShowEmptyLoaderProductTypes}
                                                    onSearch={onSearchProductTypes}
                                                    infinityScrollProps={infinityScrollProductTypesProps}
                                                    values={input.value || productTypeEmptyOption}
                                                    options={productTypeOptions}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            );
                                        }}
                                    />
                                    <Field
                                        name={EditFormKeys.Place}
                                        render={({ input, meta }) => (
                                            <InputText
                                                disabled={isLoadingMutate}
                                                label='Место проведения'
                                                placeholder='Введите место проведения'
                                                value={input.value}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Date}
                                        render={({ input, meta }) => (
                                            <Datepicker
                                                {...input}
                                                range
                                                defaultValue={input.value.value}
                                                disabled={isLoadingMutate}
                                                label='Период проведения'
                                                filled={false}
                                                placeholder='Выберите период проведения'
                                                error={meta.touched && meta.error}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Catalogs}
                                        render={({ input, meta }) => {
                                            return (
                                                <Select
                                                    required
                                                    isAsync
                                                    clearable
                                                    multiselect
                                                    disabled={isLoadingMutate}
                                                    label='Прикрепление к каталогам'
                                                    placeholder='Выберите каталоги из списка'
                                                    isShowPreloaderInList={isShowEmptyLoaderCatalogs}
                                                    onSearch={onSearchCatalogs}
                                                    infinityScrollProps={infinityScrollCatalogsProps}
                                                    values={input.value || catalogEmptyOption}
                                                    options={catalogOptions}
                                                    error={meta.touched && meta.error}
                                                    onChange={input.onChange}
                                                />
                                            );
                                        }}
                                    />
                                    <Field
                                        name={EditFormKeys.Sort}
                                        initialValue={
                                            productInfo?.sort === undefined ? undefined : String(productInfo.sort)
                                        }
                                        render={({ input, meta }) => (
                                            <InputNumber
                                                {...input}
                                                disabled={isLoadingMutate}
                                                label='Сортировка'
                                                placeholder='Введите число'
                                                min={0}
                                                hint='Чем меньше число, тем выше продукт в списке'
                                                error={meta.touched && meta.error}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.TextOrderSuccess}
                                        initialValue={productInfo?.textOrderSuccess}
                                        render={({ input, meta }) => (
                                            <Textarea
                                                required
                                                disabled={isLoadingMutate}
                                                label='Текст об успешном приобретении продукта'
                                                placeholder='Введите текст'
                                                maxLength={255}
                                                value={String(input.value)}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.TextOrderFail}
                                        initialValue={productInfo?.textOrderFail}
                                        render={({ input, meta }) => (
                                            <Textarea
                                                required
                                                disabled={isLoadingMutate}
                                                label='Текст об отказе на приобретении продукта'
                                                placeholder='Введите текст'
                                                maxLength={255}
                                                value={String(input.value)}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        )}
                                    />
                                    <Field
                                        name={EditFormKeys.Files}
                                        render={({ input, meta }) => (
                                            <InputFileSearch
                                                {...input}
                                                label='Файлы приобретенного продукта в формате PDF, DOC, DOCX, XLS, XLSX, JPEG, JPG, PNG'
                                                accept='.jpeg,.jpg,.png,.xlsx,.xls,.doc,.docx,.pdf'
                                                disabled={isLoadingMutate}
                                                placeholderLocalInput={
                                                    <Typography>
                                                        {`Перетащите в эту область или `}
                                                        <Typography color='bright_blue'>загрузите</Typography>
                                                        {`\nформата PDF, DOC, DOCX, XLS, XLSX, JPEG, JPG, PNG`}
                                                    </Typography>
                                                }
                                                error={meta.touched && meta.error}
                                                onChange={(file) => {
                                                    input.onChange(file);
                                                }}
                                            />
                                        )}
                                    />
                                </InputGroup>
                            </NetworkStateNew>
                        </Body>
                    </Modal>
                );
            }}
        />
    );
};
